const getLabel = (title) => `A ${title || ''} Services`.trim();
const openDropdowns = new Set();
const scrollLockClass = 'fc-services-dropdown-scroll-locked';
const mobileBreakpoint = 767;
const mobileViewportGap = 16;

const updateScrollLock = () => {
	const isLocked = openDropdowns.size > 0;

	document.documentElement.classList.toggle(scrollLockClass, isLocked);
	document.body?.classList.toggle(scrollLockClass, isLocked);
};

const setScrollLock = (block, isLocked) => {
	if (isLocked) {
		openDropdowns.add(block);
	} else {
		openDropdowns.delete(block);
	}

	updateScrollLock();
};

const getPanelHeight = (panelWrap, measuredContentHeight = null) => {
	if (!panelWrap) {
		return 0;
	}

	// Measure the wrapper so spacing around the panel is included as well as
	// the bordered panel itself.
	const contentHeight = measuredContentHeight ?? panelWrap.scrollHeight;

	if (window.innerWidth > mobileBreakpoint) {
		return contentHeight;
	}

	const viewportHeight = window.visualViewport?.height || window.innerHeight;
	const viewportTop = window.visualViewport?.offsetTop || 0;
	const panelTop = panelWrap.getBoundingClientRect().top - viewportTop;
	const availableHeight = Math.max(
		0,
		viewportHeight - panelTop - mobileViewportGap
	);

	return Math.min(contentHeight, availableHeight);
};

const updateOpenHeight = (block, panelWrap) => {
	if (!block.classList.contains('is-open')) {
		return;
	}

	panelWrap.style.maxHeight = `${getPanelHeight(panelWrap)}px`;
};

const setActivePanel = (block, termId) => {
	const parentButtons = Array.from(block.querySelectorAll('[data-term-target]'));
	const panels = Array.from(block.querySelectorAll('[data-term-panel]'));

	parentButtons.forEach((button) => {
		const isActive = button.dataset.termTarget === String(termId);
		button.classList.toggle('is-active', isActive);
		button.setAttribute('aria-selected', isActive ? 'true' : 'false');
		button.tabIndex = isActive ? 0 : -1;
	});

	panels.forEach((panel) => {
		const isActive = panel.dataset.termPanel === String(termId);
		panel.classList.toggle('is-active', isActive);
		panel.hidden = !isActive;
	});
};

const setPanelWrapState = (block, panelWrap, isOpen) => {
	if (!panelWrap) {
		return;
	}

	if (isOpen) {
		panelWrap.hidden = false;
		panelWrap.style.maxHeight = `${getPanelHeight(panelWrap)}px`;
		block.classList.add('is-open');
		return;
	}

	panelWrap.style.maxHeight = `${getPanelHeight(panelWrap)}px`;
	window.requestAnimationFrame(() => {
		panelWrap.style.maxHeight = '0px';
		block.classList.remove('is-open');
	});
};

const closeDropdown = (block, toggle, panelWrap) => {
	toggle.setAttribute('aria-expanded', 'false');
	setScrollLock(block, false);
	setPanelWrapState(block, panelWrap, false);
};

const openDropdown = (block, toggle, panelWrap) => {
	panelWrap.hidden = false;
	panelWrap.style.maxHeight = '0px';
	toggle.setAttribute('aria-expanded', 'true');
	setScrollLock(block, true);

	window.requestAnimationFrame(() => {
		setPanelWrapState(block, panelWrap, true);
	});
};

const initServicesDropdown = (block) => {
	if (!block || block.dataset.fcServicesDropdownInit === '1') {
		return;
	}

	const toggle = block.querySelector('.fc-services-dropdown__toggle');
	const panelWrap = block.querySelector('.fc-services-dropdown__panel-wrap');
	const close = block.querySelector('.fc-services-dropdown__close');
	const backdrop = block.querySelector('.fc-services-dropdown__backdrop');
	const parentsPicker = block.querySelector('.fc-services-dropdown__parents-picker');
	const parentsToggle = block.querySelector('.fc-services-dropdown__parents-toggle');
	const parents = block.querySelector('.fc-services-dropdown__parents');
	const activeTermId = block.dataset.activeTermId;

	if (!toggle || !panelWrap || !activeTermId) {
		return;
	}

	block.dataset.fcServicesDropdownInit = '1';
	setActivePanel(block, activeTermId);
	panelWrap.style.maxHeight = '0px';

	const setParentsExpanded = (isExpanded) => {
		if (!parentsPicker || !parentsToggle || !parents) {
			return;
		}

		const isStacked = window.innerWidth <= mobileBreakpoint;
		const pickerButtons = Array.from(parents.querySelectorAll('[data-term-target]'));

		if (!isStacked) {
			parentsPicker.classList.remove('is-expanded');
			parentsToggle.setAttribute('aria-expanded', 'false');
			parents.style.maxHeight = '';
			pickerButtons.forEach((button) => {
				button.removeAttribute('aria-hidden');
				button.inert = false;
			});
			return;
		}

		const currentParentsHeight = parents.getBoundingClientRect().height;
		const activeButton = parents.querySelector('.fc-services-dropdown__parent-button.is-active');
		parentsPicker.classList.toggle('is-expanded', isExpanded);
		parentsToggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
		parentsToggle.setAttribute(
			'aria-label',
			isExpanded ? parentsToggle.dataset.labelClose : parentsToggle.dataset.labelOpen
		);
		pickerButtons.forEach((button) => {
			const isAvailable = isExpanded || button === activeButton;
			button.setAttribute('aria-hidden', isAvailable ? 'false' : 'true');
			button.inert = !isAvailable;
		});

		const targetParentsHeight = isExpanded
			? parents.scrollHeight
			: activeButton?.offsetHeight || 0;
		const targetPanelHeight = panelWrap.scrollHeight
			- currentParentsHeight
			+ targetParentsHeight;

		parents.style.maxHeight = `${targetParentsHeight}px`;

		if (block.classList.contains('is-open')) {
			panelWrap.style.maxHeight = `${getPanelHeight(panelWrap, targetPanelHeight)}px`;
		}
	};

	setParentsExpanded(false);

	parentsToggle?.addEventListener('click', () => {
		setParentsExpanded(!parentsPicker?.classList.contains('is-expanded'));
	});

	parents?.addEventListener('transitionend', (event) => {
		if (event.propertyName === 'max-height') {
			updateOpenHeight(block, panelWrap);
		}
	});

	panelWrap.addEventListener('transitionend', (event) => {
		if (event.propertyName !== 'max-height') {
			return;
		}

		if (!block.classList.contains('is-open')) {
			panelWrap.hidden = true;
			return;
		}

		panelWrap.style.maxHeight = `${getPanelHeight(panelWrap)}px`;
	});

	toggle.addEventListener('click', () => {
		const isOpen = toggle.getAttribute('aria-expanded') === 'true';
		if (isOpen) {
			setParentsExpanded(false);
			closeDropdown(block, toggle, panelWrap);
			return;
		}

		openDropdown(block, toggle, panelWrap);
		setParentsExpanded(false);
	});

	if (close) {
		close.addEventListener('click', () => {
			setParentsExpanded(false);
			closeDropdown(block, toggle, panelWrap);
		});
	}

	if (backdrop) {
		backdrop.addEventListener('click', () => {
			setParentsExpanded(false);
			closeDropdown(block, toggle, panelWrap);
		});
	}

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && block.classList.contains('is-open')) {
			if (parentsPicker?.classList.contains('is-expanded')) {
				setParentsExpanded(false);
				parentsToggle?.focus();
				return;
			}

			closeDropdown(block, toggle, panelWrap);
		}
	});

	document.addEventListener('click', (event) => {
		if (!block.classList.contains('is-open')) {
			return;
		}

		if (!block.contains(event.target)) {
			setParentsExpanded(false);
			closeDropdown(block, toggle, panelWrap);
		}
	});

	const parentButtons = Array.from(block.querySelectorAll('[data-term-target]'));

	parentButtons.forEach((button, index) => {
		button.addEventListener('click', () => {
			setActivePanel(block, button.dataset.termTarget || activeTermId);
			setParentsExpanded(false);
			updateOpenHeight(block, panelWrap);
		});

		button.addEventListener('keydown', (event) => {
			if (!['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
				return;
			}

			event.preventDefault();

			if (event.key === 'Home') {
				parentButtons[0]?.focus();
				return;
			}

			if (event.key === 'End') {
				parentButtons[parentButtons.length - 1]?.focus();
				return;
			}

			const direction = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
			const nextIndex = (index + direction + parentButtons.length) % parentButtons.length;
			parentButtons[nextIndex]?.focus();
		});
	});

	const handleViewportResize = () => {
		setParentsExpanded(parentsPicker?.classList.contains('is-expanded') || false);
		updateOpenHeight(block, panelWrap);
	};

	window.addEventListener('resize', handleViewportResize);
	window.visualViewport?.addEventListener('resize', handleViewportResize);
};

const initAll = () => {
	if (document.body && document.body.classList.contains('wp-admin')) {
		return;
	}

	document.querySelectorAll('.wp-block-fc-block-fc-services-dropdown').forEach(initServicesDropdown);
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initAll);
} else {
	initAll();
}
